/**
 * Unit conversion helpers — mirrors wingsdiet_mobile UnitConversionService.
 * Nutrition formula: (per100gValue / 100) * totalWeightInBaseUnit
 */

const UNIT_TO_GRAM = {
  gram: 100,
  g: 100,
  kg: 1000,
  kilogram: 1000,
  ounce: 28.35,
  oz: 28.35,
  cup: 240,
  tablespoon: 14,
  spoon: 14,
  tbsp: 14,
  slice: 28,
  serving: 150,
  bowl: 250,
  piece: 100,
  whole: 100,
};

const UNIT_TO_ML = {
  ml: 100,
  milliliter: 100,
  liter: 1000,
  l: 1000,
  cup: 240,
  glass: 250,
  bottle: 500,
  can: 330,
};

export const SOLID_MEASUREMENTS = [
  "Gram",
  "KG",
  "Ounce (oz)",
  "Bowl",
  "Piece/whole",
  "Slice",
  "Serving",
  "Tablespoon / Spoon",
  "Cup",
];

export const LIQUID_MEASUREMENTS = [
  "ML",
  "Liter",
  "Cup",
  "Glass",
  "Bottle",
  "Can",
];

export function normalizeUnit(unit) {
  const lower = String(unit || "")
    .toLowerCase()
    .trim();
  if (["ml", "milliliter", "milliliters"].includes(lower)) return "ml";
  if (["l", "liter", "liters", "litre", "litres"].includes(lower)) return "liter";
  if (["glass", "glasses"].includes(lower)) return "glass";
  if (["bottle", "bottles"].includes(lower)) return "bottle";
  if (["can", "cans"].includes(lower)) return "can";
  if (["g", "grams", "gram"].includes(lower)) return "gram";
  if (["kg", "kilograms", "kilogram"].includes(lower)) return "kg";
  if (["oz", "ounces", "ounce", "ounce (oz)"].includes(lower)) return "ounce";
  if (
    [
      "tbsp",
      "tablespoons",
      "tablespoon",
      "spoon",
      "spoons",
      "tablespoon / spoon",
    ].includes(lower)
  ) {
    return "tablespoon";
  }
  if (["piece", "pieces", "whole", "wholes", "piece/whole"].includes(lower)) {
    return "piece";
  }
  if (["slice", "slices"].includes(lower)) return "slice";
  if (["serving", "servings"].includes(lower)) return "serving";
  if (["bowl", "bowls"].includes(lower)) return "bowl";
  if (["cup", "cups"].includes(lower)) return "cup";
  return lower;
}

export function isBaseUnitMl(unit) {
  const u = normalizeUnit(unit);
  return u === "ml";
}

export function isBaseUnitGram(unit) {
  const u = normalizeUnit(unit);
  return u === "gram";
}

/** 1 unit of `unit` expressed in base unit (gram or ml). */
export function getOneUnitInBaseUnit(unit, baseUnit = "gram") {
  const normalized = normalizeUnit(unit);
  const base = String(baseUnit || "gram").toLowerCase();
  if (base === "ml" || base === "milliliter") {
    return UNIT_TO_ML[normalized] ?? 1;
  }
  return UNIT_TO_GRAM[normalized] ?? 1;
}

export function isLiquidBase(baseUnit) {
  const b = String(baseUnit || "g").toLowerCase();
  return b === "ml" || b === "milliliter";
}

export function measurementsForBase(baseUnit) {
  return isLiquidBase(baseUnit) ? LIQUID_MEASUREMENTS : SOLID_MEASUREMENTS;
}

/** Scale per-100g/ml macros by total weight in base unit. */
export function calcMacroFromPer100(per100, weightInBase) {
  const p = Number(per100);
  const w = Number(weightInBase);
  if (!Number.isFinite(p) || !Number.isFinite(w) || w <= 0) return 0;
  return Math.round((p / 100) * w * 1000) / 1000;
}

/**
 * Init measurement state from food-bank detail (same defaults as mobile SearchFoodDetailScreen).
 */
export function initMeasurementFromFoodDetail(detail) {
  const macros = detail?.nutrition?.macronutrient || {};
  const per100 = {
    calories: Number(
      macros["energy-kcal"] ??
        macros.energy_kcal ??
        macros.energyKcal ??
        macros.calories ??
        0,
    ),
    fat: Number(macros.fat ?? 0),
    protein: Number(macros.proteins ?? macros.protein ?? 0),
    carbs: Number(macros.carbohydrates ?? macros.carbs ?? 0),
  };

  const serving = detail?.serving || {};
  const servingQty = Number(
    serving.serving_quantity ?? serving.servingQuantity ?? 0,
  );
  const servingUnitRaw =
    serving.serving_quantity_unit ?? serving.servingQuantityUnit ?? "g";
  const servingUnit = String(servingUnitRaw).toLowerCase().trim() || "g";

  let selectedUnit = servingUnit;
  let baseUnit = servingUnit;
  let perItemWeight;
  let selectedQuantity = 1;

  if (Number.isFinite(servingQty) && servingQty > 0) {
    // servingQuantity = weight of one item in that unit (often already grams)
    perItemWeight = servingQty;
    selectedUnit = servingUnit;
    baseUnit = isBaseUnitMl(servingUnit) ? "ml" : "g";
    // If API unit is already base, keep; else treat servingQty as already in base
    if (!isBaseUnitGram(servingUnit) && !isBaseUnitMl(servingUnit)) {
      baseUnit = "g";
    } else {
      baseUnit = isBaseUnitMl(servingUnit) ? "ml" : "g";
    }
  } else {
    selectedUnit = "g";
    baseUnit = "g";
    perItemWeight = 100;
  }

  const selectedWeight = selectedQuantity * perItemWeight;

  return {
    selectedQuantity,
    selectedWeight,
    perItemWeight,
    selectedUnit,
    baseUnit,
    per100,
  };
}

export function recalculateWeight(quantity, perItemWeight) {
  const q = Number(quantity);
  const p = Number(perItemWeight);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return 0;
  return q * p;
}

export function recalculateForNewUnit(newUnit, quantity, baseUnit) {
  const perItemWeight = getOneUnitInBaseUnit(newUnit, baseUnit);
  const selectedWeight = recalculateWeight(quantity, perItemWeight);
  return { perItemWeight, selectedWeight, selectedUnit: newUnit };
}

/**
 * Init measurement from an already-saved Food (edit modal).
 */
export function initMeasurementFromStoredFood(food = {}) {
  const tq =
    food.total_quantity && typeof food.total_quantity === "object"
      ? food.total_quantity
      : null;
  const weightRaw =
    Number(food.weight) ||
    Number(tq?.value) ||
    0;
  let quantity = Number(food.serving_size);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    quantity = 1;
  }

  let baseUnit = String(food.weight_unit || "g").toLowerCase().trim() || "g";
  if (baseUnit !== "ml" && baseUnit !== "milliliter") baseUnit = "g";
  else baseUnit = "ml";

  let selectedUnit = String(food.unit || (baseUnit === "ml" ? "ML" : "Gram"));
  const list = measurementsForBase(baseUnit);
  const match = list.find(
    (m) => m.toLowerCase() === selectedUnit.toLowerCase(),
  );
  if (match) selectedUnit = match;

  let perItemWeight =
    weightRaw > 0 && quantity > 0 ? weightRaw / quantity : weightRaw;
  if (!Number.isFinite(perItemWeight) || perItemWeight <= 0) {
    perItemWeight = getOneUnitInBaseUnit(selectedUnit, baseUnit);
  }
  const selectedWeight = quantity * perItemWeight;

  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  let caloriesPer100 = n(food.calories_per_100g);
  let fatPer100 = n(food.fat_per_100g);
  let proteinPer100 = n(food.protein_per_100g);
  let carbsPer100 = n(food.carbs_per_100g);

  // Derive per-100 from totals if missing
  if (selectedWeight > 0) {
    if (!caloriesPer100 && food.calories != null) {
      caloriesPer100 = (n(food.calories) / selectedWeight) * 100;
    }
    if (!fatPer100 && food.fat != null) {
      fatPer100 = (n(food.fat) / selectedWeight) * 100;
    }
    if (!proteinPer100 && (food.proteins != null || food.protein != null)) {
      proteinPer100 =
        (n(food.proteins ?? food.protein) / selectedWeight) * 100;
    }
    if (!carbsPer100 && food.carbs != null) {
      carbsPer100 = (n(food.carbs) / selectedWeight) * 100;
    }
  }

  // Legacy: no weight — treat stored macros as per-100g base for 100g item
  if (selectedWeight <= 0 || (!caloriesPer100 && food.calories != null)) {
    if (!caloriesPer100) caloriesPer100 = n(food.calories);
    if (!fatPer100) fatPer100 = n(food.fat);
    if (!proteinPer100) proteinPer100 = n(food.proteins ?? food.protein);
    if (!carbsPer100) carbsPer100 = n(food.carbs);
    if (selectedWeight <= 0) {
      perItemWeight = 100;
      quantity = 1;
      return {
        selectedQuantity: 1,
        selectedWeight: 100,
        perItemWeight: 100,
        selectedUnit: match || (baseUnit === "ml" ? "ML" : "Gram"),
        baseUnit,
        per100: {
          calories: caloriesPer100,
          fat: fatPer100,
          protein: proteinPer100,
          carbs: carbsPer100,
        },
      };
    }
  }

  return {
    selectedQuantity: quantity,
    selectedWeight,
    perItemWeight,
    selectedUnit,
    baseUnit,
    per100: {
      calories: caloriesPer100,
      fat: fatPer100,
      protein: proteinPer100,
      carbs: carbsPer100,
    },
  };
}
